import { Directive, inject, signal } from '@angular/core';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';
import { RelatorioFormBase } from '@/shared/components/relatorios/relatorio-form.base';
import { RelatorioExportResponse } from '@/shared/components/relatorios/relatorio-job-base';
import { Pessoa } from '@/shared/models/pessoa-info.model';
import { PessoaDomain } from '@/core/api/sicom-api/registro/pessoa/pessoa.domain';
import { PessoaService } from '@/shared/services/pessoa.service';
import { PessoaService as PessoaDadosService } from '@/shared/components/pessoa/pessoa-detalhe/pessoa.service';
import { PessoaResponse } from '@/shared/components/pessoa/pessoa-detalhe/pessoa.model';

/**
 * Extensão de `RelatorioFormBase` para relatórios de médico que dependem de um médico
 * selecionado (CRM/UF). Replica dentro de cada relatório o padrão "card + Alterar" usado em
 * `arrecadacao/relatorios/.../certidao-cadastral` (`app-pessoa-detalhe-mobile` + `app-pesquisa`),
 * eliminando o card somente-leitura que antes ficava fixo no shell de relatórios de médicos.
 */
@Directive()
export abstract class RelatorioMedicoBase<TRequest, TResponse extends RelatorioExportResponse> extends RelatorioFormBase<TRequest, TResponse> {
    /**
     * Gate do botão Gerar. Declarado aqui para valer nos 22 relatórios que herdam desta base —
     * o backend já exige esta role em `/api/v1/cadastro/medico/reports/**` (medico-routes.yml),
     * então sem o `[appCan]` o botão ficava clicável só para tomar 403.
     */
    protected override readonly roleGerar = PERMISSOES.medicos.relatorios.gerar;

    /** Sessão global do médico selecionado (sobrevive à navegação entre relatórios). */
    protected readonly pessoaService = inject(PessoaService);
    private readonly pessoaDadosService = inject(PessoaDadosService);

    /** Médico corrente exibido no card (referência mínima id/uf/crm/registro). */
    protected readonly pessoa = signal<Pessoa | undefined>(undefined);
    /** Dados completos do médico para o card; `undefined` enquanto carrega, `null` em erro. */
    protected readonly pessoaResponse = signal<PessoaResponse | null | undefined>(undefined);
    /** Visibilidade do diálogo de busca avançada (`app-pesquisa`). */
    protected readonly pesquisarDialog = signal(false);

    private pessoaLoadToken = 0;

    /** Só é possível gerar o relatório com um médico selecionado. */
    protected get possuiPessoaSelecionada(): boolean {
        return Boolean(this.pessoa());
    }

    /** Abre o diálogo de busca avançada para trocar o médico selecionado. */
    protected readonly showPesquisarPessoa = (): void => {
        this.pesquisarDialog.set(true);
    };

    /** Callback do `(pessoa)` de `app-pesquisa`: persiste na sessão e atualiza o card. */
    protected readonly loadData = (pessoa: PessoaDomain): void => {
        if (!pessoa?.id || !pessoa?.uf) return;
        this.pessoaService.setPessoa(pessoa);
        this.applyPessoa({ id: pessoa.id, uf: pessoa.uf, crm: pessoa.crm, registro: pessoa.registro, tipo: pessoa.tipo });
    };

    override ngOnInit(): void {
        super.ngOnInit();
        const pessoa = this.pessoaService.getPessoa() ?? this.pessoaService.getPessoaReference();
        if (pessoa) this.applyPessoa(pessoa);
    }

    /**
     * Monta `{ nuCrm, sgUf }` a partir do médico em sessão; avisa e retorna `null` se faltar algum dado.
     * Após um reload de página, `PessoaService.getPessoa()` (sessão em memória) volta a `null` — só
     * `getPessoaReference()` sobrevive via `sessionStorage`, e essa referência mínima não guarda CRM.
     * Por isso também caem em `this.pessoa()`, que `applyPessoa()` completa com o CRM assim que os
     * dados completos do card chegam (ver `completarCrmFaltante`), evitando o falso "sem médico
     * selecionado" mesmo com o card já exibindo o médico corretamente.
     */
    protected resolverIdentificacaoMedico(mensagemAtencao: string): { nuCrm: number; sgUf: string } | null {
        const pessoa = this.pessoaService.getPessoa();
        const pessoaReference = this.pessoaService.getPessoaReference();
        const pessoaCard = this.pessoa();
        const nuCrm = Number(pessoa?.dadosGerais?.crm || pessoa?.crm || pessoa?.registro || pessoaCard?.crm || pessoaCard?.registro || 0);
        const sgUf = pessoa?.uf || pessoaReference?.uf || pessoaCard?.uf || '';

        if (!nuCrm || !sgUf) {
            this.messageService.add({ severity: 'warn', summary: 'Atenção', detail: mensagemAtencao });
            return null;
        }
        return { nuCrm, sgUf };
    }

    private applyPessoa(pessoa: Pessoa): void {
        this.pessoa.set(pessoa);
        this.pessoaResponse.set(undefined);

        const token = ++this.pessoaLoadToken;
        this.pessoaDadosService.carregar(pessoa).subscribe({
            next: (res) => {
                if (token !== this.pessoaLoadToken) return;
                this.pessoaResponse.set(res);
                this.completarCrmFaltante(res);
            },
            error: () => {
                if (token !== this.pessoaLoadToken) return;
                this.pessoaResponse.set(null);
            }
        });
    }

    /**
     * Completa `pessoa()` com o CRM vindo dos dados completos buscados para o card quando o médico
     * foi restaurado só pela referência mínima do `sessionStorage` (sem CRM). Sem isso,
     * `resolverIdentificacaoMedico()` acusaria "médico não selecionado" mesmo com o card populado.
     */
    private completarCrmFaltante(resposta: PessoaResponse): void {
        const atual = this.pessoa();
        if (!atual || atual.crm || atual.registro) return;
        const crm = resposta.dadosGerais?.['CRM'] || resposta.dadosGerais?.['Registro'];
        if (!crm) return;
        this.pessoa.set({ ...atual, crm });
    }
}
